import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  LoadingOverlay,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { UPDATE_VEHICLE_TYPE } from "apollo/mutuations";
import { GET_VEHICLE_TYPE } from "apollo/queries";
import ImageUploader from "components/controls/ImageUpload";
import { customLoader } from "components/utilities/loader";
import { tabList } from "components/utilities/tablist";
import React, { useEffect, useRef, useState } from "react";
import { Photo } from "tabler-icons-react";

const VehicleTypeEditModal = ({ setOpenedEdit, editId }) => {
  const [updateVehicleType] = useMutation(UPDATE_VEHICLE_TYPE);
  const [typeDropDownData, setTypeDropDownData] = useState([]);
  const [existingImage, setExistingImage] = useState(null); // Existing image URL
  const [file, setFile] = useState(null); // Uploaded image file
  const form = useForm({
    initialValues: {
      title: "",
      starting_price: "",
      price_per_kilometer: "",
      type: "",
    },
    validate: {
      starting_price: (value) => {
        if (!value && value !== 0) return "Required";
        if (Number(value) < 0) return "Price cannot be negative";
        if (isNaN(Number(value))) return "Must be a number";
        return null;
      },
      price_per_kilometer: (value) => {
        if (!value && value !== 0) return "Required";
        if (Number(value) < 0) return "Price cannot be negative";
        if (isNaN(Number(value))) return "Must be a number";
        return null;
      },
    },
  });

  useEffect(() => {
    let types = ["Shipment", "Dropoff"];
    let type = types.map((item) => ({
      label: item,
      value: item,
    }));
    setTypeDropDownData(type);
  }, []);

  const { loading } = useQuery(GET_VEHICLE_TYPE, {
    variables: { id: editId },
    onCompleted(data) {
      form.setValues({
        id: editId,
        starting_price: data.vehicleType.starting_price,
        price_per_kilometer: data.vehicleType.price_per_kilometer,
        type: data.vehicleType.type,
        title: data.vehicleType.title,
      });
      setExistingImage(data.vehicleType.image);
    },
  });

  const [activeTab, setActiveTab] = useState(tabList[0].value);
  const { height } = useViewportSize();

  const submit = () => {
    let variables = {
      id: form.getInputProps("id").value,
      type: form.getInputProps("type").value,
      starting_price: parseFloat(form.values.starting_price),
      price_per_kilometer: parseFloat(form.values.price_per_kilometer),
      title: form.getInputProps("title").value,
    };
    if (file && file.length > 0) {
      variables.image = file[file.length - 1];
    }
    updateVehicleType({
      variables,
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: "Vehicle type edited successfully",
        });
        form.reset();
        setFile(null);
        setOpenedEdit(false);
      },
      onError(err) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            err?.graphQLErrors?.[0]?.message || "Failed to update Vehicle Type",
        });
      },
    });
  };

  const setTypeDropDownValue = (val) => {
    form.setFieldValue("type", val);
  };

  return (
    <Tabs color="blue" value={activeTab} onTabChange={setActiveTab}>
      <LoadingOverlay
        visible={loading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(() => submit())} noValidate>
          <Stack>
            <Grid>
              <Grid.Col span={4}>
                <TextInput
                  required
                  label="Vehicle Type "
                  placeholder="Enter Vehicle Type"
                  {...form.getInputProps("title")}
                />
                <TextInput
                  required
                  label="Starting Price"
                  placeholder="Starting Price"
                  type="number"
                  min={0}
                  step="any"
                  {...form.getInputProps("starting_price")}
                  onKeyDown={(e) => {
                    if (
                      e.key === "-" ||
                      e.key === "Minus" ||
                      e.key === "NumpadSubtract"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </Grid.Col>
              <Grid.Col span={4}>
                <Select
                  data={typeDropDownData}
                  value={form.getInputProps("type")?.value.toString()}
                  onChange={setTypeDropDownValue}
                  label="Type"
                  placeholder="Pick a Type this Vehicle Type belongs to"
                />
                <TextInput
                  required
                  label="Price Per Kilometer"
                  placeholder="Price Per Kilometer"
                  type="number"
                  min={0}
                  step="any"
                  {...form.getInputProps("price_per_kilometer")}
                  onKeyDown={(e) => {
                    if (
                      e.key === "-" ||
                      e.key === "Minus" ||
                      e.key === "NumpadSubtract"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={12}>
                <ImageUploader
                  defaultImage={existingImage}
                  multiple={false}
                  accept="image/png, image/jpeg, image/jpg"
                  onChange={(selectedFiles) => {
                    setFile(selectedFiles);
                  }}
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={12}>
                <Button
                  type="submit"
                  style={{
                    marginTop: "10px",
                    width: "20%",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  fullWidth
                  color="blue"
                >
                  Submit
                </Button>
              </Grid.Col>
            </Grid>
          </Stack>
        </form>
      </ScrollArea>
    </Tabs>
  );
};

export default VehicleTypeEditModal;
